import { RegistrationPanel } from '../components/RegistrationPanel.js';
import { createLogger } from '../game/utils/Logger.js';

const logger = createLogger('StoryPage');

/**
 * Check if running in Electron (has electronAPI) or web browser (Vercel)
 */
function isElectron(): boolean {
    return typeof window.electronAPI !== 'undefined';
}

// GitHub releases page for downloading the Electron client
const DOWNLOAD_URL = 'https://github.com/starlightweave-labs/aetherweave-gambit/releases';

document.addEventListener('DOMContentLoaded', () => {
    // Bind Weave Buttons - behavior differs between Electron and Web
    const weaveButtons = document.querySelectorAll<HTMLElement>('.btn-weave:not(.btn-lite)');

    if (isElectron()) {
        // ELECTRON MODE: Show login/register panel
        const authPanel = new RegistrationPanel();
        weaveButtons.forEach((btn: HTMLElement) => {
            btn.addEventListener('click', (e: Event) => {
                e.preventDefault();
                authPanel.open();
            });
        });
        logger.info('Running in Electron mode - auth panel enabled');
    } else {
        // WEB MODE: Redirect to download page
        weaveButtons.forEach((btn: HTMLElement) => {
            btn.textContent = 'DOWNLOAD GAME';
            btn.addEventListener('click', (e: Event) => {
                e.preventDefault();
                window.open(DOWNLOAD_URL, '_blank');
            });
        });
        logger.info('Running in Web mode - download redirect enabled');
    }

    // Intersection Observer for Scroll Animations
    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible');
            }
        });
    }, {
        threshold: 0.1
    });

    document.querySelectorAll('.timeline-entry').forEach((el: Element) => observer.observe(el));

    // Dynamic SVG Weave Height
    const container = document.querySelector('.story-container') as HTMLElement | null;
    const svg = document.querySelector('.weave-svg') as SVGSVGElement | null;
    const path = document.querySelector('.weave-path') as SVGPathElement | null;

    function updateWeave(): void {
        if (!container || !svg || !path) return;

        const height = container.offsetHeight;
        svg.setAttribute('viewBox', `0 0 100 ${height}`);
        // Simple sine wave generation
        let d = `M 50 0`;
        const freq = 0.02;
        const amp = 30;
        for (let i = 0; i <= height; i += 10) {
            const x = 50 + Math.sin(i * freq) * amp;
            d += ` L ${x} ${i}`;
        }
        path.setAttribute('d', d);
    }

    window.addEventListener('resize', updateWeave);
    updateWeave();


    // Slider Logic bound within module scope
    let currentSlide = 0;
    const slides = document.querySelectorAll<HTMLElement>('.hero-showcase');

    function showSlide(index: number): void {
        if (slides.length === 0) return;

        // Wrap around
        if (index >= slides.length) currentSlide = 0;
        else if (index < 0) currentSlide = slides.length - 1;
        else currentSlide = index;

        // Toggle active class
        slides.forEach((slide: HTMLElement, i: number) => {
            slide.classList.remove('active');
            if (i === currentSlide) {
                slide.classList.add('active');

                // Apply orientation-specific classes
                const showcaseVisuals = slide.querySelector('.showcase-visuals') as HTMLElement | null;
                if (showcaseVisuals !== null && showcaseVisuals !== undefined) {
                    // Remove existing orientation classes
                    showcaseVisuals.classList.remove('vertical-card', 'horizontal-card');

                    // Slide 0: Planar Walker (vertical card)
                    // Slide 1: Serpent Tamer (horizontal card)
                    if (i === 0) {
                        showcaseVisuals.classList.add('vertical-card');
                    } else if (i === 1) {
                        showcaseVisuals.classList.add('horizontal-card');
                    }
                }

                // Reset video if present to autoplay
                const video = slide.querySelector('video') as HTMLVideoElement | null;
                if (video !== null && video !== undefined) {
                    video.currentTime = 0;
                    video.play().catch((e: Error) => logger.debug('Autoplay prevented', { error: e.message }));
                }
            }
        });
    }

    // Bind Arrows (scoped to showcase-slider to avoid conflict with civ-carousel)
    const leftArrow = document.querySelector('.showcase-slider .slider-arrow.left') as HTMLElement | null;
    const rightArrow = document.querySelector('.showcase-slider .slider-arrow.right') as HTMLElement | null;

    if (leftArrow !== null && leftArrow !== undefined) leftArrow.addEventListener('click', () => showSlide(currentSlide - 1));
    if (rightArrow !== null && rightArrow !== undefined) rightArrow.addEventListener('click', () => showSlide(currentSlide + 1));

    // Initialize first slide with proper orientation
    showSlide(0);

    // =========================================================================
    // CIVILIZATION CAROUSEL
    // =========================================================================
    let currentCivSlide = 0;
    const civSlides = document.querySelectorAll<HTMLElement>('.civ-slide');
    const civDots = document.querySelectorAll<HTMLElement>('.civ-dot');

    function showCivSlide(index: number): void {
        if (civSlides.length === 0) return;

        // Wrap around
        if (index >= civSlides.length) currentCivSlide = 0;
        else if (index < 0) currentCivSlide = civSlides.length - 1;
        else currentCivSlide = index;

        // Toggle active class on slides
        civSlides.forEach((slide: HTMLElement, i: number) => {
            slide.classList.remove('active');
            if (i === currentCivSlide) {
                slide.classList.add('active');

                // Autoplay video if present in tribe cards
                const videos = slide.querySelectorAll<HTMLVideoElement>('video');
                videos.forEach((video: HTMLVideoElement) => {
                    video.currentTime = 0;
                    video.play().catch((e: Error) => logger.debug('Video autoplay prevented', { error: e.message }));
                });
            }
        });

        // Toggle active class on dots
        civDots.forEach((dot: HTMLElement, i: number) => {
            dot.classList.remove('active');
            if (i === currentCivSlide) {
                dot.classList.add('active');
            }
        });
    }

    // Bind Civilization Carousel Arrows
    const civLeftArrow = document.getElementById('civ-arrow-left');
    const civRightArrow = document.getElementById('civ-arrow-right');

    if (civLeftArrow !== null && civLeftArrow !== undefined) {
        civLeftArrow.addEventListener('click', () => showCivSlide(currentCivSlide - 1));
    }
    if (civRightArrow !== null && civRightArrow !== undefined) {
        civRightArrow.addEventListener('click', () => showCivSlide(currentCivSlide + 1));
    }

    // Bind Civilization Dots
    civDots.forEach((dot: HTMLElement) => {
        dot.addEventListener('click', () => {
            const index = parseInt(dot.dataset['index'] ?? '0', 10);
            showCivSlide(index);
        });
    });

    // Initialize first civilization slide
    showCivSlide(0);
});
